import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import type { BridgeMessage } from "@hotwired/hotwire-native-bridge"

interface NavTab {
  key: string
  title: string
  icon: string
  url: string
}

interface Declaration {
  version: number
  tabs: NavTab[]
  activeKey: string | null
}

export default class extends BridgeComponent {
  static component = "navigation"
  static values = {
    tabs: Array,
    activeKey: String,
    // The shape of the declaration. The page states it; a page that omits
    // it sends 0, which the integration test guards against.
    version: Number,
  }

  declare readonly tabsValue: NavTab[]
  declare readonly activeKeyValue: string
  declare readonly versionValue: number

  #connected = false
  #declarationQueued = false

  connect() {
    super.connect()
    this.#connected = true
    this.declare()
  }

  disconnect() {
    this.#connected = false
    super.disconnect()
  }

  // What native holds is what this mount currently carries, so a render that
  // rewrites the values in place declares even though nothing reconnected.
  // Without this the model depends on every listener navigation being a
  // full-body Turbo visit: adopt morphing anywhere and the bar goes stale in
  // silence, because absence means "keep the last declaration" (the contract's
  // "Absence is not a signal"; design D4 of native-navigation-bridge-1719).
  tabsValueChanged() {
    this.#redeclare()
  }

  activeKeyValueChanged() {
    this.#redeclare()
  }

  versionValueChanged() {
    this.#redeclare()
  }

  // The bridge routes a reply to the callback registered with its message,
  // so `receive` only runs if connect hands it one.
  declare() {
    // Spread because an interface carries no implicit index signature, and
    // `send` takes a `Record<string, unknown>`.
    this.send("connect", { ...this.#declaration }, (message: BridgeMessage) =>
      this.receive(message),
    )
  }

  // Stimulus starts its value observer before calling connect(), so every value
  // on the element reports a change on the way in: declaring from here
  // unguarded would announce the same page three times. Waiting for connect()
  // keeps that the single first declaration.
  //
  // The declaration is then deferred to a microtask, which is what makes one
  // render one declaration: a render rewriting both values produces both
  // mutations in a single observer batch, and the batch drains before the
  // microtask runs. Deferring rather than comparing payloads keeps every
  // arrival a declaration — the bar reconciles on what arrives, and an
  // arrival withheld is precisely what D3 cannot afford.
  #redeclare() {
    if (!this.#connected || this.#declarationQueued) return

    this.#declarationQueued = true
    queueMicrotask(() => {
      this.#declarationQueued = false
      if (this.#connected) this.declare()
    })
  }

  get #declaration(): Declaration {
    return {
      version: this.versionValue,
      tabs: this.tabsValue,
      activeKey: this.activeKeyValue || null,
    }
  }

  receive(message: BridgeMessage) {
    if (message.event !== "tabSelected") return

    // The destination is resolved from the tabs this page declared, so the
    // address visited is always one the server named.
    const tab = this.tabsValue.find((t) => t.key === message.data.key)
    if (!tab) {
      console.warn(`bridge-navigation: unknown tab ${String(message.data.key)}`)
      this.declare()
      return
    }

    window.Turbo.visit(tab.url, { action: "replace" })
  }
}
